/**
 * 평면화 데이터 → Array/String 스켈레톤 트리 + Module lazy 인덱스
 */
import {
  moduleDomainToTreeFields,
  moduleFromApi,
  stringDomainToTreeFields,
  stringFromFlatRecord
} from './panelHierarchyMappers.js';
import {
  clearPanelTreeModuleChildrenIndex,
  setPanelTreeModuleChildrenIndex
} from './panelTreeModuleChildrenIndex.js';
import { comparePanelTreeListItems } from './panelModuleSeqOrder.js';
import { freezeLazyPanelTreeSkeleton } from './freezeLazyPanelTreeSkeleton.js';

function buildModuleTreeNodes(stringModules) {
  return stringModules.map((module, moduleIndex) => {
    const moduleDomain = moduleFromApi(module);
    return {
      ...moduleDomainToTreeFields(moduleDomain),
      name: `Module ${moduleDomain.displayNo || moduleIndex + 1}`,
      children: []
    };
  });
}

function buildArrayNode(array, strings, modules) {
  const displayId = array.arrayId || array.id;
  const arrayNode = {
    name: array.name || `Array ${displayId}`,
    id: `array_${array.id}`,
    type: 'array',
    arrayId: displayId,
    solarArraySeq: array.id,
    solarArrayId: array.id,
    children: []
  };

  const arrayStrings = strings
    .filter(string => string.parentId === array.id)
    .sort(comparePanelTreeListItems);
  const indexEntries = [];

  arrayStrings.forEach((string, stringIndex) => {
    const stringDomain = stringFromFlatRecord(string);
    const stringNode = {
      ...stringDomainToTreeFields(stringDomain),
      name: string.name || `String ${stringDomain.displayNo || stringIndex + 1}`,
      children: [],
      lazyModules: false,
      moduleChildCount: 0
    };

    const stringModules = modules.filter(module => module.parentId === string.id);
    const moduleNodes = buildModuleTreeNodes(stringModules).sort(comparePanelTreeListItems);
    stringNode.moduleChildCount = moduleNodes.length;
    if (moduleNodes.length > 0) {
      stringNode.lazyModules = true;
      indexEntries.push([stringNode.id, moduleNodes]);
    }

    arrayNode.children.push(stringNode);
  });

  return { arrayNode, indexEntries };
}

/**
 * @returns {Array} derivedTreeData 스켈레톤 (Module은 expand 시 hydrate)
 */
export function buildLazyPanelTreeFromFlattened(sections, arrays, strings, modules) {
  clearPanelTreeModuleChildrenIndex();
  const treeData = [];
  const index = new Map();

  const sectionIdSet = new Set((sections || []).map(s => s.id));
  const arraysUnderSection = sectionId =>
    arrays.filter(array => array.parentId === sectionId).sort(comparePanelTreeListItems);

  const pushArray = array => {
    const { arrayNode, indexEntries } = buildArrayNode(array, strings, modules);
    indexEntries.forEach(([id, nodes]) => index.set(id, nodes));
    treeData.push(arrayNode);
  };

  if (sections?.length) {
    sections.forEach(section => {
      arraysUnderSection(section.id).forEach(pushArray);
    });
  }

  const rootArrays = arrays
    .filter(array => !sectionIdSet.has(array.parentId))
    .sort(comparePanelTreeListItems);
  rootArrays.forEach(pushArray);

  setPanelTreeModuleChildrenIndex(index);
  return freezeLazyPanelTreeSkeleton(treeData);
}
